/**
 * List parser — groups consecutive numbered/bulleted paragraphs into list wrapper nodes.
 *
 * In OOXML, list items are just regular paragraphs with <w:numPr> in their <w:pPr>.
 * OOXML has no explicit list container — consecutive paragraphs sharing the same
 * numId form a list, and the ilvl (indent level) determines nesting.
 *
 * ProseMirror requires explicit wrapper nodes:
 *   bulletList / orderedList
 *     listItem
 *       paragraph (the original paragraph content)
 *
 * This module performs a post-processing pass over flat paragraph nodes,
 * grouping consecutive list paragraphs into proper nested list structures.
 *
 * **Outline numbering:** Numbered headings (e.g. "1. Scope", "2. Terms") and
 * outline-numbered paragraphs (e.g. "9.1 Pre-Existing Materials") are NOT
 * wrapped in ordered lists. Headings are separated by body text (breaking
 * consecutive grouping), and outline numbering (levelText like "%1.%2.")
 * can't be rendered by TipTap's orderedList. Instead, the numbering text
 * is prepended directly into the node content.
 */

import { PMNode, ParseContext, NumberingDefinition } from './types';

/**
 * Process an array of body-level nodes and group consecutive list paragraphs
 * into bulletList/orderedList wrapper nodes.
 *
 * Non-list nodes pass through unchanged. This is a post-processing step
 * applied after all paragraphs have been parsed.
 */
export function groupListNodes(nodes: PMNode[], ctx: ParseContext): PMNode[] {
  // First pass: inline numbering text for headings and outline-numbered paragraphs.
  // - Headings: always inlined (separated by body text, can't group consecutively)
  // - Outline-numbered paragraphs: inlined when levelText references multiple
  //   levels (e.g. "%1.%2.") because TipTap's orderedList can't render "9.1" etc.
  // Counters are shared across both so cross-level references resolve correctly.
  const preprocessed = inlineOutlineNumbers(nodes, ctx);

  const result: PMNode[] = [];
  let i = 0;

  while (i < preprocessed.length) {
    const node = preprocessed[i];
    const listInfo = getListInfo(node);

    if (!listInfo) {
      // Not a list paragraph — pass through
      result.push(node);
      i++;
      continue;
    }

    // Start of a list run — collect consecutive paragraphs with the same numId
    const listParagraphs: { node: PMNode; level: number; numId: string }[] = [];

    while (i < preprocessed.length) {
      const current = preprocessed[i];
      const info = getListInfo(current);
      if (!info || info.numId !== listInfo.numId) break;
      listParagraphs.push({
        node: current,
        level: info.level,
        numId: info.numId,
      });
      i++;
    }

    // Determine list type from numbering definition
    const listType = getListType(listInfo.numId, 0, ctx);

    // Get start value for ordered lists
    const startValue = getListStart(listInfo.numId, 0, ctx);

    // Build nested list structure
    const listNode = buildListTree(
      listParagraphs,
      0,
      listType,
      ctx,
      startValue
    );
    result.push(listNode);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Outline numbering — inline pass
// ---------------------------------------------------------------------------

/**
 * Check if a levelText pattern is "outline" numbering — i.e., it references
 * a level other than its own. E.g., level 1 with levelText "%1.%2." references
 * level 0 (%1) in addition to its own level (%2).
 *
 * Simple single-level patterns like "%1." or "%2)" at their own level are NOT
 * outline numbering and can be handled by TipTap's orderedList.
 */
function isOutlineNumbering(levelText: string, level: number): boolean {
  const re = /%(\d+)/g;
  let match: RegExpExecArray | null;
  let hasRefs = false;
  while ((match = re.exec(levelText)) !== null) {
    hasRefs = true;
    const refLevel = parseInt(match[1], 10) - 1;
    if (refLevel !== level) return true;
  }
  return false;
}

/**
 * Pre-process nodes to inline numbering text for:
 * 1. Headings with any numbering (they're separated by body text, can't group)
 * 2. Paragraphs with outline numbering (levelText like "%1.%2." can't be
 *    rendered by TipTap's orderedList)
 *
 * Tracks counters per (numId, level) across ALL numbered nodes so that
 * cross-level references resolve correctly (e.g., "%1" in a level-1 paragraph
 * picks up the heading counter at level 0).
 */
function inlineOutlineNumbers(nodes: PMNode[], ctx: ParseContext): PMNode[] {
  // Track counters: key = "numId:level", value = current count
  const counters = new Map<string, number>();

  return nodes.map((node) => {
    if (node.type !== 'heading' && node.type !== 'paragraph') return node;

    const numId = node.attrs?._numId as string | undefined;
    const level = (node.attrs?._numLevel as number) ?? 0;
    if (!numId || numId === '0') return node;

    const numDef = ctx.numbering.get(numId);
    if (!numDef) return node;

    const levelDef = numDef.levels.find((l) => l.level === level);
    if (!levelDef) return node;

    // Skip bullet-type numbering
    if (isBulletFormat(levelDef.numFmt, levelDef.levelText)) {
      return node;
    }

    const isHeading = node.type === 'heading';
    const isOutline = isOutlineNumbering(levelDef.levelText, level);

    // Only inline for: (a) headings, or (b) paragraphs with outline numbering
    if (!isHeading && !isOutline) {
      // Regular numbered paragraph — increment counter but leave for list grouping
      const counterKey = `${numId}:${level}`;
      const startVal = levelDef.start || 1;
      const currentCount = (counters.get(counterKey) ?? (startVal - 1)) + 1;
      counters.set(counterKey, currentCount);
      resetDeeperCounters(counters, numId, level);
      return node;
    }

    // Increment counter for this (numId, level)
    const counterKey = `${numId}:${level}`;
    const startVal = levelDef.start || 1;
    const currentCount = (counters.get(counterKey) ?? (startVal - 1)) + 1;
    counters.set(counterKey, currentCount);
    // Word restarts every deeper level when a parent level increments, so the
    // first sub-clause under "2." is "2.1", not a continuation ("2.3"). Reset
    // all deeper counters for this numId.
    resetDeeperCounters(counters, numId, level);

    // Build the numbering prefix from levelText pattern
    const prefix = formatLevelText(levelDef.levelText, level, numId, counters, ctx);

    // Strip attrs: headings keep marginLeft (no list CSS), paragraphs also keep it
    // since they won't be wrapped in a list container either
    const cleanNode = stripHeadingNumAttrs(node);

    // Prepend numbering text to existing content
    const prefixNode: PMNode = { type: 'text', text: prefix + ' ' };
    const content = cleanNode.content
      ? [prefixNode, ...cleanNode.content]
      : [prefixNode];

    return { ...cleanNode, content };
  });
}

/**
 * Reset the counters for all levels DEEPER than `level` within `numId` (Word
 * restarts sublevels each time a parent level advances). Levels 0–8 cover
 * Word's 9-level maximum.
 */
function resetDeeperCounters(
  counters: Map<string, number>,
  numId: string,
  level: number
): void {
  for (let deeper = level + 1; deeper <= 8; deeper++) {
    counters.delete(`${numId}:${deeper}`);
  }
}

/** Check if a numbering format is a bullet type. */
function isBulletFormat(numFmt: string, levelText: string): boolean {
  return (
    numFmt === 'bullet' ||
    levelText === '' ||
    levelText === '•' ||
    levelText === '○' ||
    levelText === '■'
  );
}

/**
 * Format a levelText pattern like "%1.", "%1.%2.", etc.
 * Replace each %N placeholder with the current counter value for that level,
 * formatted according to the level's numFmt.
 */
function formatLevelText(
  levelText: string,
  currentLevel: number,
  numId: string,
  counters: Map<string, number>,
  ctx: ParseContext
): string {
  return levelText.replace(/%(\d+)/g, (_match, levelNumStr) => {
    const refLevel = parseInt(levelNumStr, 10) - 1; // %1 → level 0
    const counterKey = `${numId}:${refLevel}`;
    const value = counters.get(counterKey) ?? 1;

    // Look up numFmt for the referenced level
    const numDef = ctx.numbering.get(numId);
    const refLevelDef = numDef?.levels.find((l) => l.level === refLevel);
    const numFmt = refLevelDef?.numFmt ?? 'decimal';

    return formatNumber(value, numFmt);
  });
}

/**
 * Format a number according to an OOXML numFmt.
 */
function formatNumber(value: number, numFmt: string): string {
  switch (numFmt) {
    case 'decimal':
    case 'decimalZero':
      return String(value);
    case 'upperLetter':
      return String.fromCharCode(64 + ((value - 1) % 26) + 1);
    case 'lowerLetter':
      return String.fromCharCode(96 + ((value - 1) % 26) + 1);
    case 'upperRoman':
      return toRoman(value).toUpperCase();
    case 'lowerRoman':
      return toRoman(value).toLowerCase();
    default:
      return String(value);
  }
}

/** Simple integer to Roman numeral converter. */
function toRoman(num: number): string {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['M', 'CM', 'D', 'CD', 'C', 'XC', 'L', 'XL', 'X', 'IX', 'V', 'IV', 'I'];
  let result = '';
  for (let i = 0; i < vals.length; i++) {
    while (num >= vals[i]) {
      result += syms[i];
      num -= vals[i];
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface ListInfo {
  numId: string;
  level: number;
}

/**
 * Extract list membership info from a paragraph node's attrs.
 * Returns null if the node is not a list paragraph.
 *
 * Note: headings are excluded here because numbered headings are handled
 * by inlineHeadingNumbers() in the pre-processing pass. Only paragraphs
 * participate in list grouping.
 */
function getListInfo(node: PMNode): ListInfo | null {
  if (node.type !== 'paragraph') return null;

  const numId = node.attrs?._numId as string | undefined;
  const level = (node.attrs?._numLevel as number) ?? 0;

  if (!numId || numId === '0') return null;

  return { numId, level };
}

/**
 * Determine whether a numbering ID at a given level is a bullet or ordered list.
 */
function getListType(
  numId: string,
  level: number,
  ctx: ParseContext
): 'bulletList' | 'orderedList' {
  const numDef = ctx.numbering.get(numId);
  if (!numDef) return 'bulletList'; // default to bullet

  const levelDef = numDef.levels.find((l) => l.level === level);
  if (!levelDef) return 'bulletList';

  // Bullet formats
  if (
    levelDef.numFmt === 'bullet' ||
    levelDef.levelText === '' ||
    levelDef.levelText === '•' ||
    levelDef.levelText === '○' ||
    levelDef.levelText === '■'
  ) {
    return 'bulletList';
  }

  return 'orderedList';
}

/**
 * Get the start number for an ordered list from the numbering definition.
 * Returns 1 (default) for bullet lists or if the definition isn't found.
 */
function getListStart(
  numId: string,
  level: number,
  ctx: ParseContext
): number {
  const numDef = ctx.numbering.get(numId);
  if (!numDef) return 1;

  const levelDef = numDef.levels.find((l) => l.level === level);
  if (!levelDef) return 1;

  return levelDef.start || 1;
}

/**
 * Build a nested list tree from a flat array of list paragraphs.
 *
 * Algorithm: Process paragraphs left-to-right. When we encounter a paragraph
 * at a deeper level than the current list, we recursively create a nested
 * sublist and attach it to the last list item.
 */
function buildListTree(
  paragraphs: { node: PMNode; level: number; numId: string }[],
  baseLevel: number,
  listType: 'bulletList' | 'orderedList',
  ctx: ParseContext,
  startValue?: number
): PMNode {
  const items: PMNode[] = [];
  let i = 0;

  while (i < paragraphs.length) {
    const { node, level } = paragraphs[i];

    if (level === baseLevel) {
      // This paragraph is at our level — create a listItem
      const paraNode = stripListAttrs(node);
      const listItem: PMNode = {
        type: 'listItem',
        content: [paraNode],
      };
      items.push(listItem);
      i++;
    } else if (level > baseLevel) {
      // Deeper level — collect all consecutive deeper paragraphs
      const subParagraphs: {
        node: PMNode;
        level: number;
        numId: string;
      }[] = [];
      while (i < paragraphs.length && paragraphs[i].level > baseLevel) {
        subParagraphs.push(paragraphs[i]);
        i++;
      }

      // Determine the sub-list type and start value
      const subListType =
        subParagraphs.length > 0
          ? getListType(subParagraphs[0].numId, subParagraphs[0].level, ctx)
          : listType;
      const subStartValue =
        subParagraphs.length > 0
          ? getListStart(
              subParagraphs[0].numId,
              subParagraphs[0].level,
              ctx
            )
          : 1;

      // Build nested list
      const subList = buildListTree(
        subParagraphs,
        baseLevel + 1,
        subListType,
        ctx,
        subStartValue
      );

      // Attach to the last list item (or create a new one if no items yet)
      if (items.length > 0) {
        const lastItem = items[items.length - 1];
        if (!lastItem.content) lastItem.content = [];
        lastItem.content.push(subList);
      } else {
        // No parent item — create an empty listItem to hold the sub-list
        items.push({
          type: 'listItem',
          content: [{ type: 'paragraph' }, subList],
        });
      }
    } else {
      // Shallower level — shouldn't happen in well-formed input, but handle gracefully
      // by treating as same level
      const paraNode = stripListAttrs(node);
      items.push({
        type: 'listItem',
        content: [paraNode],
      });
      i++;
    }
  }

  const listNode: PMNode = {
    type: listType,
    content: items,
  };

  const listAttrs: Record<string, unknown> = {};

  // Set start attribute for ordered lists with non-default start values
  if (listType === 'orderedList' && startValue && startValue !== 1) {
    listAttrs.start = startValue;
  }

  // Transfer marginLeft from the first paragraph to the list wrapper so the
  // entire list (bullets/numbers + text) is indented to match the DOCX layout.
  // Individual paragraphs have their marginLeft stripped by stripListAttrs to
  // avoid double-indentation.
  if (paragraphs.length > 0) {
    const firstMargin = paragraphs[0].node.attrs?.marginLeft as
      | string
      | undefined;
    if (firstMargin) {
      listAttrs.marginLeft = firstMargin;
    }
  }

  if (Object.keys(listAttrs).length > 0) {
    listNode.attrs = listAttrs;
  }

  return listNode;
}

/**
 * Remove only internal numbering tracking attributes (_numId, _numLevel) from a heading.
 * Unlike stripListAttrs, this preserves marginLeft/textIndent because the heading
 * is NOT wrapped in a TipTap list container — there's no list CSS to provide indentation,
 * so we must keep the DOCX's original indentation.
 */
function stripHeadingNumAttrs(node: PMNode): PMNode {
  if (!node.attrs) return node;

  const { _numId, _numLevel, ...cleanAttrs } = node.attrs as Record<
    string,
    unknown
  >;

  return {
    ...node,
    attrs: Object.keys(cleanAttrs).length > 0 ? cleanAttrs : undefined,
  };
}

/**
 * Remove internal list tracking attributes (_numId, _numLevel) and
 * OOXML indentation attributes (marginLeft, textIndent) from a paragraph
 * before placing it in the final document tree.
 *
 * In OOXML, list paragraphs carry explicit indentation (left indent + hanging
 * indent) to accommodate the list number/bullet. TipTap's bulletList/orderedList
 * CSS handles this natively, so preserving these attrs causes the number to
 * overlap with the text content.
 */
function stripListAttrs(node: PMNode): PMNode {
  if (!node.attrs) return node;

  const {
    _numId,
    _numLevel,
    marginLeft,
    textIndent,
    ...cleanAttrs
  } = node.attrs as Record<string, unknown>;

  return {
    ...node,
    attrs: Object.keys(cleanAttrs).length > 0 ? cleanAttrs : undefined,
  };
}
